import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import type { Cabinet, EdgeBand, Garnitur, Id } from "@/lib/types";
import { getCabinetClass } from "@/lib/cabinets";
import { getParametricClass } from "@/lib/cabinets/dsl/registry";
import { evaluateClass } from "@/lib/cabinets/dsl/evaluate";
import { evaluateAllDrawers } from "@/lib/cabinets/drawers";
import { evaluateAllLegs } from "@/lib/cabinets/legs";
import type { CatalogIndex } from "@/lib/cabinets/bom";
import { validateCabinet } from "@/lib/cabinets/validation";
import { PartsPreview2D } from "./PartsPreview2D";
import { DrawersSection } from "./DrawersSection";
import { LegsSection } from "./LegsSection";

interface Props {
  cabinet: Cabinet | null;
  /** Project's garniturs, used to populate the reassignment dropdown. */
  garniturs?: Garnitur[];
  /** Id of the garnitur the selected cabinet currently lives in (computed
   *  by the parent from the project tree). null = no parent / nothing
   *  selected. */
  currentGarniturId?: Id | null;
  /** Move the selected cabinet into a different garnitur (tree re-parent).
   *  Distinct from `onChange` because nested cabinets don't carry a
   *  garniturId field — re-parenting is a tree-shape mutation. */
  onMoveToGarnitur?: (targetGarniturId: Id) => void;
  /** Catalog index + edge bands list for the per-part editor. */
  catalog?: CatalogIndex;
  edgeBands?: EdgeBand[];
  onChange: (changes: Partial<Cabinet>) => void;
  onDelete: () => void;
  /** Open the (page-level) PartEditor modal, optionally pre-selected on
   *  a given partId. Hosted by ProjectEditorPage so it works even when
   *  the right rail shows DrawerInspector / PartInspector instead of
   *  CabinetEditor. */
  onRequestPartEditor?: (initialPartId?: string) => void;
}

/**
 * Right rail — properties of the currently-selected cabinet.
 * Edits are debounced (200ms) so dragging a slider doesn't hammer ddb.
 */
export function CabinetEditor({ cabinet, garniturs = [], currentGarniturId = null, onMoveToGarnitur, catalog, onChange, onDelete, onRequestPartEditor }: Props) {
  // Live evaluation for the in-rail 2D preview — runs the class against
  // this cabinet's W/H/D + classParams + partOverrides so the preview
  // matches exactly what the 3D scene + BOM see.
  const previewMeshes = useMemo(() => {
    if (!cabinet) return [];
    const klass = getParametricClass(cabinet.classId);
    if (!klass) return [];
    try {
      const base = evaluateClass(
        {
          width: cabinet.width,
          height: cabinet.height,
          depth: cabinet.depth,
          classParams: cabinet.classParams ?? {},
          partOverrides: cabinet.partOverrides ?? {},
        },
        klass,
      ).meshes;
      // Drawers layer on top — same pattern as CabinetMesh and BOM.
      const drawers = evaluateAllDrawers(cabinet).meshes;
      const legs = evaluateAllLegs(cabinet).meshes;
      return [...base, ...drawers, ...legs];
    } catch {
      return [];
    }
  }, [cabinet]);

  function openPartEditorOn(partId: string) {
    onRequestPartEditor?.(partId);
  }

  if (!cabinet) {
    return (
      <aside style={{ borderLeft: "1px solid var(--line)", padding: 16 }}>
        <div className="label">CABINET</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 18 }}>
          Click a cabinet in the scene to edit it.
        </div>
      </aside>
    );
  }

  const cls = getCabinetClass(cabinet.classId);
  const bounds = cls?.bounds ?? {
    w: [6, 96],
    h: [6, 120],
    d: [6, 36],
  };

  return (
    <aside style={{ borderLeft: "1px solid var(--line)", padding: 16, overflowY: "auto" }}>
      <div className="label">CABINET</div>
      <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
        {cls?.label ?? cabinet.classId}
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        ID #{cabinet._id} · {cabinet.anchor}
      </div>

      {previewMeshes.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <PartsPreview2D
            meshes={previewMeshes}
            cabinetW={cabinet.width}
            cabinetH={cabinet.height}
            cabinetD={cabinet.depth}
            selectedId={null}
            onSelect={openPartEditorOn}
            height={180}
            compact
          />
        </div>
      )}

      <DimRow
        label="Width"
        value={cabinet.width}
        min={bounds.w[0]}
        max={bounds.w[1]}
        onChange={(v) => onChange({ width: v })}
      />
      <DimRow
        label="Height"
        value={cabinet.height}
        min={bounds.h[0]}
        max={bounds.h[1]}
        onChange={(v) => onChange({ height: v })}
      />
      <DimRow
        label="Depth"
        value={cabinet.depth}
        min={bounds.d[0]}
        max={bounds.d[1]}
        onChange={(v) => onChange({ depth: v })}
      />

      <div style={{ marginTop: 16 }}>
        <div className="label">Position (inches)</div>
        <PositionRow cabinet={cabinet} onChange={onChange} />
      </div>

      <ClassParamsSection cabinet={cabinet} onChange={onChange} />

      <DrawersSection cabinet={cabinet} onChange={onChange} />

      <LegsSection cabinet={cabinet} onChange={onChange} />

      {garniturs.length > 0 && onMoveToGarnitur && (
        <div style={{ marginTop: 16 }}>
          <div className="label">Garnitur</div>
          <select
            value={currentGarniturId == null ? "" : String(currentGarniturId)}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "") return;       // can't unassign — every cabinet lives in some garnitur
              const target = Number(v);
              if (target !== currentGarniturId) onMoveToGarnitur(target);
            }}
            style={{ width: "100%", marginTop: 4 }}
          >
            {garniturs.map((g) => (
              <option key={g._id} value={g._id}>{g.name} ({g.type})</option>
            ))}
          </select>
        </div>
      )}

      {catalog && (() => {
        const issues  = validateCabinet(cabinet, catalog);
        const errors  = issues.filter((i) => i.severity === "error").length;
        const warns   = issues.length - errors;
        return (
          <>
            <button
              style={{ marginTop: 16, width: "100%" }}
              onClick={() => onRequestPartEditor?.()}
              title="Edit edges and drillings per part"
            >
              Edit parts ({Object.keys(cabinet.partOverrides ?? {}).length} overridden)
            </button>
            {(errors + warns) > 0 && (
              <div
                style={{
                  marginTop: 8,
                  padding: "6px 10px",
                  fontSize: 11,
                  borderRadius: 4,
                  background: errors > 0 ? "rgba(255,80,80,0.10)" : "rgba(255,170,85,0.10)",
                  color: errors > 0 ? "var(--danger)" : "var(--accent, #ffaa55)",
                  cursor: "pointer",
                }}
                onClick={() => onRequestPartEditor?.()}
                title="Open the part editor to inspect each issue"
              >
                ⚠ {errors > 0 && <>{errors} error{errors === 1 ? "" : "s"}</>}
                {errors > 0 && warns > 0 && " · "}
                {warns > 0 && <>{warns} warning{warns === 1 ? "" : "s"}</>}
              </div>
            )}
          </>
        );
      })()}

      <button
        className="ghost"
        onClick={onDelete}
        style={{ marginTop: 24, color: "var(--danger)", borderColor: "var(--danger)" }}
      >
        Delete cabinet
      </button>

    </aside>
  );
}

interface DimRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}

function DimRow({ label, value, min, max, onChange }: DimRowProps) {
  const [local, setLocal] = useState(value);

  useEffect(() => { setLocal(value); }, [value]);

  // Debounce edits so drag-to-resize doesn't flood the server.
  useEffect(() => {
    if (local === value) return;
    const t = setTimeout(() => { onChange(local); }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  function handle(e: ChangeEvent<HTMLInputElement>) {
    const v = Number(e.target.value);
    if (!Number.isFinite(v)) return;
    setLocal(Math.max(min, Math.min(max, v)));
  }

  return (
    <div style={{ marginTop: 12 }}>
      <label className="label">{label} <span className="muted" style={{ float: "right" }}>{local.toFixed(1)}″</span></label>
      <input
        type="range"
        min={min}
        max={max}
        step={0.5}
        value={local}
        onChange={handle}
        style={{ width: "100%" }}
      />
      <input
        type="number"
        min={min}
        max={max}
        step={0.25}
        value={local}
        onChange={handle}
        style={{ width: "100%", marginTop: 4 }}
      />
    </div>
  );
}

// ── Class params (per-cabinet overrides) ───────────────────────
//
// Each numeric param the cabinet's ParametricCabinetClass exposes can
// be overridden on this cabinet alone. Expression-valued params
// (derived like `carcassH = "H - TOE_KICK_H"`) are hidden — overriding
// a derived param would silently break formulas that referenced it.

function ClassParamsSection({
  cabinet,
  onChange,
}: {
  cabinet: Cabinet;
  onChange: (c: Partial<Cabinet>) => void;
}) {
  const klass = getParametricClass(cabinet.classId);
  if (!klass) return null;

  // Show only directly-numeric params. Designers can still tweak
  // expression-derived params by editing the class itself.
  const editable = Object.entries(klass.params).filter(([, v]) => typeof v === "number");
  if (!editable.length) return null;

  const overrides = cabinet.classParams ?? {};

  function setParam(name: string, raw: string) {
    const next = { ...overrides };
    if (raw === "") delete next[name];
    else {
      const v = Number(raw);
      if (!Number.isFinite(v)) return;
      next[name] = v;
    }
    onChange({ classParams: Object.keys(next).length ? next : undefined });
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div className="label">Class params</div>
      <div className="muted" style={{ fontSize: 10, marginTop: 2, marginBottom: 6 }}>
        Override class defaults for this cabinet only.
      </div>
      <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
        <tbody>
          {editable.map(([name, def]) => {
            const cur = overrides[name];
            const isOverridden = cur !== undefined;
            return (
              <tr key={name}>
                <td style={{ padding: "2px 0", color: "var(--fg-dim)" }}>
                  {name}
                </td>
                <td style={{ padding: "2px 0", color: "var(--fg-dim)", textAlign: "right", paddingRight: 6 }}>
                  {typeof def === "number" ? def : "—"}
                </td>
                <td style={{ padding: "2px 0" }}>
                  <input
                    type="number"
                    step={0.125}
                    placeholder={String(def)}
                    value={isOverridden ? String(cur) : ""}
                    onChange={(e) => setParam(name, e.target.value)}
                    style={{ width: "100%", fontSize: 11, padding: "2px 4px" }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PositionRow({ cabinet, onChange }: { cabinet: Cabinet; onChange: (c: Partial<Cabinet>) => void }) {
  const [pos, setPos] = useState<[number, number, number]>(cabinet.position);
  useEffect(() => { setPos(cabinet.position); }, [cabinet.position]);

  function field(axis: 0 | 1 | 2) {
    return (
      <input
        type="number"
        step={1}
        value={pos[axis]}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (!Number.isFinite(v)) return;
          const next: [number, number, number] = [...pos];
          next[axis] = v;
          setPos(next);
          onChange({ position: next });
        }}
        style={{ width: "33%" }}
      />
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 4 }}>
      {field(0)}
      {field(1)}
      {field(2)}
    </div>
  );
}
